import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchBlotter, retrievePlayer, selectors } from 'state';

type RefreshEffectProps = {
  scenarioDate?: string | null;
  enabled: boolean;
  onRefresh: () => void;
};

/** Detects a real period-date transition so initial hydration never causes a duplicate order request. */
export const shouldRefreshSimplifiedOptionOrders = (
  previousDate: string | null | undefined,
  scenarioDate: string | null | undefined,
  enabled: boolean,
) =>
  enabled &&
  previousDate != null &&
  scenarioDate != null &&
  previousDate !== scenarioDate;

/** Runs one supplied refresh after passed_time changes the scenario date, keeping the trigger independently testable. */
export const SimplifiedOptionOrderRefreshEffect: React.FC<
  RefreshEffectProps
> = ({ scenarioDate, enabled, onRefresh }) => {
  const previousDate = useRef(scenarioDate);

  useEffect(() => {
    if (
      shouldRefreshSimplifiedOptionOrders(
        previousDate.current,
        scenarioDate,
        enabled,
      )
    ) {
      onRefresh();
    }
    previousDate.current = scenarioDate;
  }, [enabled, onRefresh, scenarioDate]);

  return null;
};

/** Rehydrates the trainee's visible order store after T1 finalizes immutable option payoffs. */
export const TraineeSimplifiedOptionOrderRefresh: React.FC = () => {
  const dispatch = useDispatch();
  const scenarioDate = useSelector(selectors.activeSession)?.scenario_date;
  const enabled = useSelector(selectors.hasSimplifiedOptionsGroup());
  const refresh = useCallback(() => {
    dispatch(fetchBlotter({}));
  }, [dispatch]);

  return (
    <SimplifiedOptionOrderRefreshEffect
      scenarioDate={scenarioDate}
      enabled={enabled}
      onRefresh={refresh}
    />
  );
};

type TrainerRefreshProps = {
  playerId: number;
};

/** Rehydrates an open trainer player drawer after T1 so its shared blotter shows finalized payoffs immediately. */
export const TrainerSimplifiedOptionOrderRefresh: React.FC<
  TrainerRefreshProps
> = ({ playerId }) => {
  const dispatch = useDispatch();
  const scenarioDate = useSelector(selectors.activeSession)?.scenario_date;
  const enabled = useSelector(selectors.hasSimplifiedOptionsGroup());
  const refresh = useCallback(() => {
    dispatch(retrievePlayer({ player_id: playerId }));
  }, [dispatch, playerId]);

  return (
    <SimplifiedOptionOrderRefreshEffect
      scenarioDate={scenarioDate}
      enabled={enabled}
      onRefresh={refresh}
    />
  );
};
